import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Database_Procs_AdminMoveGetSchoolTeams_Item } from '@suntex.fim/api-client';
import { useFramework } from '@suntex.fim/framework';

import { useSchoolContext } from '../context';

export interface UseSchoolTeamsResult {
  teams: Database_Procs_AdminMoveGetSchoolTeams_Item[];
  isLoading: boolean;
  isError: boolean;
  reload: () => void;
}

/**
 * Loads the current season's teams for the school in the surrounding
 * `SchoolProvider`. Skipped when no school id is available.
 */
export function useSchoolTeams(): UseSchoolTeamsResult {
  const { schoolId } = useSchoolContext();
  const { api, calendar } = useFramework();
  const queryClient = useQueryClient();
  const year = calendar.getCurrentSeason();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['school', 'teams', schoolId, year],
    queryFn: () => api.get_School_Teams({ schoolId: schoolId!, year }),
    enabled: schoolId !== null,
  });

  const reload = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['school', 'teams', schoolId, year],
    });
  }, [queryClient, schoolId, year]);

  return {
    teams: data?.teams ?? [],
    isLoading,
    isError,
    reload,
  };
}
